import React, { useState, useEffect, useMemo } from 'react';
import { supabase } from '../../services/supabaseClient';
import { formatDateTimeFullDDMMYYYY } from '../../utils/dateFormatter';
import {
  Search,
  ChevronLeft,
  ChevronRight,
  Loader2,
  FileText,
  AlertCircle,
  RefreshCw
} from 'lucide-react';

// Interface for audit log entry
export interface AuditLog {
  id: string;
  usuario: string;
  accion: string;
  fecha: string;
  detalle: string;
  created_at?: string;
}

interface AuditLogViewerProps {
  itemsPerPage?: number;
  autoRefresh?: boolean;
  refreshInterval?: number; // in milliseconds
}

const AuditLogViewer: React.FC<AuditLogViewerProps> = ({
  itemsPerPage = 10,
  autoRefresh = false,
  refreshInterval = 30000 // 30 seconds default
}) => {
  const [logs, setLogs] = useState<AuditLog[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [refreshing, setRefreshing] = useState<boolean>(false);

  // Fetch audit logs from Supabase
  const fetchLogs = async (showRefreshing = false) => {
    if (showRefreshing) {
      setRefreshing(true);
    } else {
      setLoading(true);
    }
    setError(null);

    try {
      // ✅ COMENTADO: No hacer SELECT a tabla auditoria
      /*
      const { data, error: supabaseError } = await supabase
        .from('auditoria')
        .select('*')
        .order('fecha', { ascending: false })
        .order('created_at', { ascending: false }); // Ordenar también por created_at como respaldo

      if (supabaseError) {
        // Check if table doesn't exist
        if (supabaseError.code === 'PGRST116' || supabaseError.message.includes('does not exist')) {
          setError('La tabla de auditoría no existe. Por favor, créela en Supabase.');
          setLogs([]);
        } else {
          setError(supabaseError.message || 'Error al cargar los logs de auditoría');
          console.error('Error fetching audit logs:', supabaseError);
        }
      } else {
        setLogs(data || []);
      }
      */
      
      // ✅ CORREGIDO: No cargar desde tabla auditoria, mostrar mensaje informativo
      // La auditoría se genera automáticamente desde la tabla gastos
      setError('La auditoría se genera automáticamente desde los gastos registrados. Use la vista de auditoría en Caja Chica.');
      setLogs([]);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Error desconocido al cargar logs';
      setError(errorMessage);
      console.error('Error fetching audit logs:', err);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  // Initial fetch
  useEffect(() => {
    fetchLogs();
  }, []);

  // Auto-refresh if enabled
  useEffect(() => {
    if (!autoRefresh) return;

    const interval = setInterval(() => {
      fetchLogs(true);
    }, refreshInterval);

    return () => clearInterval(interval);
  }, [autoRefresh, refreshInterval]);

  // Filter logs based on search query
  const filteredLogs = useMemo(() => {
    if (!searchQuery.trim()) {
      return logs;
    }

    const query = searchQuery.toLowerCase();
    return logs.filter(
      (log) =>
        log.usuario?.toLowerCase().includes(query) ||
        log.accion?.toLowerCase().includes(query) ||
        log.detalle?.toLowerCase().includes(query)
    );
  }, [logs, searchQuery]);

  // Pagination calculations
  const totalPages = Math.ceil(filteredLogs.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedLogs = filteredLogs.slice(startIndex, endIndex);

  // Reset to first page when search changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery]);

  // Format date using utility function
  const formatDate = formatDateTimeFullDDMMYYYY;

  // Get action color
  const getActionColor = (accion: string): string => {
    const actionUpper = accion.toUpperCase();
    
    if (actionUpper.includes('CREAR') || actionUpper.includes('CREATE')) {
      return 'bg-green-100 text-green-800 border-green-200';
    }
    if (actionUpper.includes('EDITAR') || actionUpper.includes('EDIT') || actionUpper.includes('UPDATE')) {
      return 'bg-blue-100 text-blue-800 border-blue-200';
    }
    if (actionUpper.includes('ELIMINAR') || actionUpper.includes('DELETE') || actionUpper.includes('REMOVE')) {
      return 'bg-red-100 text-red-800 border-red-200';
    }
    if (actionUpper.includes('APROBAR') || actionUpper.includes('APPROVE')) {
      return 'bg-yellow-100 text-yellow-800 border-yellow-200';
    }
    
    // Default color for other actions
    return 'bg-gray-100 text-gray-800 border-gray-200';
  };

  // Get action icon color
  const getActionIconColor = (accion: string): string => {
    const actionUpper = accion.toUpperCase();
    
    if (actionUpper.includes('CREAR') || actionUpper.includes('CREATE')) {
      return 'text-green-600';
    }
    if (actionUpper.includes('EDITAR') || actionUpper.includes('EDIT') || actionUpper.includes('UPDATE')) {
      return 'text-blue-600';
    }
    if (actionUpper.includes('ELIMINAR') || actionUpper.includes('DELETE') || actionUpper.includes('REMOVE')) {
      return 'text-red-600';
    }
    if (actionUpper.includes('APROBAR') || actionUpper.includes('APPROVE')) {
      return 'text-yellow-600';
    }
    
    return 'text-gray-600';
  };

  // Handle pagination
  const handlePreviousPage = () => {
    setCurrentPage((prev) => Math.max(1, prev - 1));
  };

  const handleNextPage = () => {
    setCurrentPage((prev) => Math.min(totalPages, prev + 1));
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  // Handle manual refresh
  const handleRefresh = () => {
    fetchLogs(true);
  };

  // Loading state
  if (loading) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 shadow-sm">
        <div className="p-12 text-center">
          <Loader2 className="w-8 h-8 animate-spin text-blue-600 mx-auto mb-4" />
          <p className="text-gray-600 font-medium">Cargando logs de auditoría...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="bg-white rounded-lg border border-red-200 shadow-sm">
        <div className="p-12 text-center">
          <AlertCircle className="w-12 h-12 text-red-600 mx-auto mb-4" />
          <p className="text-red-800 font-semibold mb-2">Error al cargar logs</p>
          <p className="text-sm text-red-600 mb-4">{error}</p>
          <button
            onClick={handleRefresh}
            className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Reintentar
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg border border-gray-200 shadow-sm">
      {/* Header with search and refresh */}
      <div className="p-6 border-b border-gray-200 bg-gray-50">
        <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <FileText className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900">Logs de Auditoría</h2>
              <p className="text-sm text-gray-600">
                {filteredLogs.length} {filteredLogs.length === 1 ? 'registro' : 'registros'}
                {searchQuery && ` encontrados`}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-3 w-full sm:w-auto">
            {/* Search Input */}
            <div className="flex-1 sm:flex-initial relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Buscar por usuario o acción..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full sm:w-64 pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-colors text-sm"
                aria-label="Buscar logs de auditoría"
              />
            </div>

            {/* Refresh Button */}
            <button
              onClick={handleRefresh}
              disabled={refreshing}
              className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
              aria-label="Actualizar logs"
              title="Actualizar"
            >
              <RefreshCw className={`w-5 h-5 ${refreshing ? 'animate-spin' : ''}`} />
            </button>
          </div>
        </div>
      </div>

      {/* Table */}
      {filteredLogs.length === 0 ? (
        <div className="p-12 text-center">
          <FileText className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-600 font-medium mb-2">No se encontraron logs</p>
          <p className="text-sm text-gray-500">
            {searchQuery
              ? 'Intenta ajustar los términos de búsqueda'
              : 'No hay registros de auditoría disponibles'}
          </p>
        </div>
      ) : (
        <>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                    Usuario
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                    Acción
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                    Fecha
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                    Detalle
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {paginatedLogs.map((log) => (
                  <tr
                    key={log.id}
                    className="hover:bg-gray-50 transition-colors"
                  >
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-8 w-8 bg-gray-200 rounded-full flex items-center justify-center">
                          <span className="text-xs font-medium text-gray-600">
                            {log.usuario?.charAt(0).toUpperCase() || '?'}
                          </span>
                        </div>
                        <div className="ml-3">
                          <p className="text-sm font-medium text-gray-900">
                            {log.usuario || 'Sin usuario'}
                          </p>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${getActionColor(
                          log.accion
                        )}`}
                      >
                        <span className={`w-1.5 h-1.5 rounded-full mr-1.5 ${getActionIconColor(log.accion).replace('text-', 'bg-')}`}></span>
                        {log.accion}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                      {formatDate(log.fecha || log.created_at || '')}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900 max-w-md">
                      <div className="truncate" title={log.detalle}>
                        {log.detalle || 'Sin detalle'}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="px-6 py-4 border-t border-gray-200 bg-gray-50">
              <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                <div className="text-sm text-gray-700">
                  Mostrando <span className="font-medium">{startIndex + 1}</span> a{' '}
                  <span className="font-medium">
                    {Math.min(endIndex, filteredLogs.length)}
                  </span>{' '}
                  de <span className="font-medium">{filteredLogs.length}</span> registros
                </div>

                <div className="flex items-center gap-2">
                  <button
                    onClick={handlePreviousPage}
                    disabled={currentPage === 1}
                    className="p-2 border border-gray-300 rounded-lg hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500"
                    aria-label="Página anterior"
                  >
                    <ChevronLeft className="w-5 h-5 text-gray-600" />
                  </button>

                  <div className="flex items-center gap-1">
                    {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => {
                      // Show first page, last page, current page, and pages around current
                      if (
                        page === 1 ||
                        page === totalPages ||
                        (page >= currentPage - 1 && page <= currentPage + 1)
                      ) {
                        return (
                          <button
                            key={page}
                            onClick={() => handlePageChange(page)}
                            className={`px-3 py-1.5 text-sm font-medium rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                              currentPage === page
                                ? 'bg-blue-600 text-white'
                                : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                            }`}
                            aria-label={`Ir a página ${page}`}
                            aria-current={currentPage === page ? 'page' : undefined}
                          >
                            {page}
                          </button>
                        );
                      } else if (
                        page === currentPage - 2 ||
                        page === currentPage + 2
                      ) {
                        return (
                          <span key={page} className="px-2 text-gray-400">
                            ...
                          </span>
                        );
                      }
                      return null;
                    })}
                  </div>

                  <button
                    onClick={handleNextPage}
                    disabled={currentPage === totalPages}
                    className="p-2 border border-gray-300 rounded-lg hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500"
                    aria-label="Página siguiente"
                  >
                    <ChevronRight className="w-5 h-5 text-gray-600" />
                  </button>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default AuditLogViewer;

